import { useEffect, useState } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Timer, Lightning } from "@phosphor-icons/react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function AutoApproveSettings() {
  const [config, setConfig] = useState({ enabled: false, interval_minutes: 60 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings/auto-approve`, { headers: getAuthHeaders() })
      .then((res) => setConfig(res.data))
      .catch((err) => logger.error("Auto-approve settings error:", err));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/auto-approve`, {
        enabled: config.enabled,
        interval_minutes: config.interval_minutes
      }, { headers: getAuthHeaders() });
      toast.success("Auto-approve settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally { setSaving(false); }
  };

  const intervals = [
    { label: "15 min", value: 15 },
    { label: "30 min", value: 30 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 120 },
    { label: "6 hours", value: 360 },
  ];

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="auto-approve-settings">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(245, 158, 11, 0.1)" }}>
          <Lightning size={20} weight="duotone" className="text-[#F59E0B]" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Auto-Approve Withdrawals
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">
            Automatically approve withdrawals under ₹5,000 on a schedule
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-md border border-white/10 bg-white/[0.02]">
          <div>
            <p className="text-xs text-white">Enable Auto-Approve</p>
            <p className="text-[10px] text-[#A1A1AA]">Withdrawals under ₹5,000 will be approved automatically</p>
          </div>
          <Switch
            data-testid="auto-approve-toggle"
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block flex items-center gap-1">
            <Timer size={12} /> Check Interval
          </label>
          <div className="flex gap-2 flex-wrap" data-testid="interval-buttons">
            {intervals.map((int) => (
              <button
                key={int.value}
                data-testid={`interval-${int.value}`}
                onClick={() => setConfig({ ...config, interval_minutes: int.value })}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                  config.interval_minutes === int.value
                    ? "bg-[#F59E0B] text-black"
                    : "border border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/20"
                }`}
              >
                {int.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-3 rounded-md border border-white/5 bg-white/[0.01]">
          <p className="text-[10px] text-[#A1A1AA]">
            <strong className="text-white">How it works:</strong> Every {config.interval_minutes} minutes, the system checks for pending withdrawals under ₹5,000 and automatically approves them. Notifications and audit logs are created for each batch.
          </p>
        </div>

        <button
          data-testid="save-auto-approve"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-md text-xs font-bold text-black bg-[#F59E0B] hover:bg-[#D97706] transition-colors disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
